import {
  useAdminAddRegisterMutation,
  useAdminUpdateRegisterMutation,
} from '@/modules/admin-dashboard/queries/registers.tsx';
import RegisterClassesSelectPaginate, {
  ClassItemOption,
} from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { onlyNumbers } from '@/modules/common/utils/only-numbers.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  ELEVEN_DIGITS_WITH_SEPARATOR_REGEX,
  LETTERS_ONLY_NAME_COMPANY_REGEX,
  NAME_COMPANY_REGEX,
  NINE_DIGITS_WITH_SEPARATOR_REGEX,
  REGISTER_OFFICE_REGEX,
  SIX_DIGITS_WITH_SEPARATOR_REGEX,
} from '@/modules/common/utils/regex.ts';
import { CountryNameCodeEnum } from '@/modules/entity/static/countries.ts';
import {
  BankModel,
  BankTypeEnum,
  CountryCodeEnum,
  CreateRegisterRequest,
  CurrencyEnum,
  RegisterByIdResponse,
  RegisterType,
  RoundingType,
  UpdateRegisterRequest,
} from 'api';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import { isArray } from 'lodash';
import { ChangeEvent, Fragment, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  Button,
  Dropzone,
  ModalContentWrapper,
  Select,
  SelectButtonOption,
  SelectButtons,
  SelectPaginateOption,
  Spinner,
  TabButtons,
  Tabs,
  TextInput,
  TextInputField,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { BankBlackIcon, PlusBlueIcon, TrashIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import * as yup from 'yup';

const sharesTypeOptions: SelectButtonOption<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const validationSchema = yup.object<UpdateRegisterRequest>({
  banks: yup.array().of(
    yup.object<BankModel>({
      id: yup.number().nullable().optional(),
      bank_account_name: yup
        .string()
        .trim()
        .required('This field is required')
        .max(255, 'Maximum length 255'),
      bank_account_bsb: yup
        .string()
        .trim()
        .matches(
          SIX_DIGITS_WITH_SEPARATOR_REGEX,
          'Invalid BSB, must be 6 digits with optional dash or space',
        )
        .required('This field is required'),
      bank_account_number: yup
        .string()
        .trim()
        .matches(
          NINE_DIGITS_WITH_SEPARATOR_REGEX,
          'Invalid account number, must be 9 digits with optional dash or space',
        )
        .required('This field is required'),
      bank_name: yup
        .string()
        .trim()
        .required('This field is required')
        .uppercase('Must be large characters')
        .length(3, 'Length must be 3 characters'),
      bank_types: yup
        .array()
        .of(
          yup
            .mixed<BankTypeEnum>()
            .oneOf(Object.values(BankTypeEnum))
            .required('This field is required'),
        )
        .required('This field is required'),
      bank_currency: yup
        .mixed<CurrencyEnum>()
        .oneOf(Object.values(CurrencyEnum))
        .required('This field is required'),
      bank_classes: yup.array().of(yup.number()).nullable().optional(),
    }),
  ),
  user_identification_number: yup.number().max(999999, 'Maximum length 6').nullable(),
  // .required('This field is required'),
  email: yup.string().trim().email('Email must be a valid email'),
  phone: yup.string().trim().min(3).max(20).nullable(),
  country_code: yup.string().trim().required('This field is required'),
  registered_office: yup
    .string()
    .trim()
    .matches(
      REGISTER_OFFICE_REGEX,
      'Incorrect address, example of correct one: 111/11 Name Street, North Sydney NSW 1111',
    )
    .required('This field is required'),
  website: yup.string().trim().nullable(),
  abn: yup
    .string()
    .trim()
    .when('country_code', ([country_code], schema) => {
      if (country_code === 'AUS') {
        return schema
          .matches(
            ELEVEN_DIGITS_WITH_SEPARATOR_REGEX,
            'Incorrect ABN, example of correct one: 12345678901',
          )
          .required('This field is required');
      }

      return schema.nullable();
    }),
  ACN: yup
    .string()
    .trim()
    .when('country_code', ([country_code], schema) => {
      if (country_code === 'AUS') {
        return schema
          .matches(
            NINE_DIGITS_WITH_SEPARATOR_REGEX,
            'Incorrect ACN, example of correct one: 123456789',
          )
          .required('This field is required');
      }

      return schema.nullable();
    }),
  trustee: yup
    .string()
    .trim()
    .max(70, 'The name is too long')
    .matches(
      LETTERS_ONLY_NAME_COMPANY_REGEX,
      'Incorrect name, should contain only letters, whitespaces and symbols - and _',
    )
    .when('type', ([type], schema) => {
      if (type !== RegisterType.Company) {
        return schema.required('This field is required.');
      }
      return schema;
    }),
  allows_fractional_shares: yup.boolean().required('This select is required'),
  type: yup
    .mixed<RegisterType>()
    .oneOf(Object.values(RegisterType))
    .required('This field is required'),
  name: yup
    .string()
    .trim()
    .matches(NAME_COMPANY_REGEX, 'Incorrect name, should begin with the words')
    .required('This field is required'),
  register_legal_name: yup
    .string()
    .trim()
    .max(70, 'The name is too long')
    .matches(
      LETTERS_ONLY_NAME_COMPANY_REGEX,
      'Incorrect name, should contain only letters, whitespaces and symbols - and _',
    )
    .nullable(),
  code: yup.string().trim().nullable(),
  icon_base64: yup.string().trim().nullable(),
  rounding_type: yup
    .mixed<RoundingType>()
    .when('allows_fractional_shares', ([allows_fractional_shares], schema) => {
      return allows_fractional_shares
        ? schema.nullable()
        : schema.oneOf(Object.values(RoundingType)).required('This field is required');
    }),
});

export enum TabItem {
  General = 'General',
  Contacts = 'Contacts',
  BankAccounts = 'Bank accounts',
}

const initialValues: UpdateRegisterRequest & { currency?: CurrencyEnum } = {
  email: '',
  phone: '',
  website: '',
  country_code: '' as CountryCodeEnum,
  registered_office: '',
  abn: '',
  ACN: '',
  trustee: '',
  allows_fractional_shares: sharesTypeOptions[1].value,
  type: '' as RegisterType,
  name: '',
  register_legal_name: '',
  code: 'hardcoded',
  id: undefined as unknown as number,
  icon_base64: undefined,
  user_identification_number: undefined,
  rounding_type: null as unknown as RoundingType,
  hold_carried_forward: sharesTypeOptions[1].value,
  banks: [],
};

const normalizeEmptyStringValues = <T,>(value: T): T => {
  if (typeof value === 'string') {
    return (value.trim() === '' ? '' : value) as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeEmptyStringValues(item)) as T;
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, normalizeEmptyStringValues(nestedValue)]),
    ) as T;
  }

  return value;
};

export interface AdminAddRegisterModalProps {
  redactionRegisterId?: number;
  open: boolean;
  onClose: () => void;
  role?: 'admin' | 'issuer';
  isFinishRegistrationMode?: boolean;
}

export const AdminRegisterModal = ({
  open,
  redactionRegisterId,
  onClose,
  role = 'admin',
  isFinishRegistrationMode = false,
}: AdminAddRegisterModalProps) => {
  const disabledTooltipId = useId();
  const [activeTab, setActiveTab] = useState<TabItem>(TabItem.General);
  const [currentIcon, setCurrentIcon] = useState('');

  const [isFileLoading, setIsFileLoading] = useState(false);
  const isRedaction = !!redactionRegisterId;

  const {
    data: redactionRegisterData,
    isFetching: isLoadingRedactedRegister,
    isFetched: isRedactionRegisterFetched,
  } = useRegisterQuery(redactionRegisterId || 0, !redactionRegisterId);

  const formik = useFormik<UpdateRegisterRequest & { currency?: CurrencyEnum }>({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload: UpdateRegisterRequest & { currency?: CurrencyEnum } = {
        ...values,
        abn: values.abn.replaceAll(/([ -])/gm, ''),
        ACN: values.ACN.replaceAll(/([ -])/gm, ''),
        banks: values.banks.map(item => ({
          ...item,
          bank_account_bsb: item.bank_account_bsb.replaceAll(/([ -])/gm, ''),
          bank_account_number: item.bank_account_number.replaceAll(/([ -])/gm, ''),
        })),
        code: values.name[0],
      };

      if (isRedaction && redactionRegisterId) {
        await updateRegister({ ...payload, id: redactionRegisterId } as UpdateRegisterRequest & {
          currency?: CurrencyEnum;
        });
      } else {
        await addRegister(payload as CreateRegisterRequest);
      }
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: addRegister, isLoading: isRegisterCreation } = useAdminAddRegisterMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: updateRegister, isLoading: isRegisterUpdating } =
    useAdminUpdateRegisterMutation({
      setErrors: formik.setErrors,
    });

  const handleChangeSelect = useCallback(
    (field: string) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  const handleCloseModalWithResetForm = () => {
    formik.resetForm();
    onClose();
  };

  useEffect(() => {
    if (isRedactionRegisterFetched && redactionRegisterData?.data) {
      const register = normalizeEmptyStringValues(redactionRegisterData.data);

      Object.entries(register).forEach(([key, value]) => {
        if ((key as keyof RegisterByIdResponse) === 'icon_url') {
          setCurrentIcon(value);
          return;
        }
        if ((key as keyof RegisterByIdResponse) === 'register_bank') {
          formik.setFieldValue('banks', value);
          return;
        }
        formik.setFieldValue(key, value); // todo: check
      });
    }
  }, [isRedactionRegisterFetched, redactionRegisterData?.data]);

  useEffect(() => {
    if (isFinishRegistrationMode && isRedactionRegisterFetched) {
      setTimeout(() => formik.validateForm(), 0);
    }
  }, [isFinishRegistrationMode, isRedactionRegisterFetched]);

  const title = useMemo(() => {
    if (!isRedaction) return 'Add new register';

    if (isLoadingRedactedRegister || !redactionRegisterData?.data) return <Spinner />;

    if (isFinishRegistrationMode) return 'Finish your registration';

    return (
      <div>
        Edit <span className="text-blue-700">{redactionRegisterData?.data.name}</span>
      </div>
    );
  }, [isFinishRegistrationMode, isLoadingRedactedRegister, isRedaction, redactionRegisterData?.data]);

  const onFileAccepted = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await formik.setFieldValue('icon_base64', base64);
      formik.setFieldError('icon_base64', '');

      setIsFileLoading(false);
    },
    [formik],
  );

  const onFileDelete = useCallback(async () => {
    handleChangeSelect('icon_base64')(null);
    setCurrentIcon('');
    if (redactionRegisterId) {
      await updateRegister({
        ...formik.values,
        id: redactionRegisterId,
        icon_base64: '',
      } as unknown as UpdateRegisterRequest & { currency?: CurrencyEnum });
    }
  }, [formik.values, handleChangeSelect, redactionRegisterId, updateRegister]);

  const tabItems = useMemo(
    () => [
      {
        name: TabItem.General,
        container: (
          <GeneralBlock
            values={formik.values}
            errors={formik.errors}
            redactionRegisterId={redactionRegisterId}
            isLoadingRedactedRegister={isLoadingRedactedRegister}
            onClose={onClose}
            open={open}
            isFileLoading={isFileLoading}
            onFileAccepted={onFileAccepted}
            onFileDelete={onFileDelete}
            handleChangeSelect={handleChangeSelect}
            iconUrl={currentIcon}
            role={role}
          />
        ),
        isError:
          !!formik.errors.name ||
          !!formik.errors.icon_base64 ||
          !!formik.errors.type ||
          !!formik.errors.country_code ||
          !!formik.errors.registered_office ||
          !!formik.errors.ACN ||
          !!formik.errors.abn ||
          !!formik.errors.trustee ||
          !!formik.errors.user_identification_number ||
          !!formik.errors.allows_fractional_shares,
      },
      {
        name: TabItem.Contacts,
        container: (
          <ContactBlock
            values={formik.values}
            errors={formik.errors}
            redactionRegisterId={redactionRegisterId}
            isLoadingRedactedRegister={isLoadingRedactedRegister}
            onClose={onClose}
            open={open}
            isFileLoading={isFileLoading}
            onFileAccepted={onFileAccepted}
            onFileDelete={onFileDelete}
            handleChangeSelect={handleChangeSelect}
            iconUrl={currentIcon}
            role={role}
          />
        ),
        isError: !!formik.errors.email || !!formik.errors.phone,
      },
      {
        name: TabItem.BankAccounts,
        container: (
          <BankDetailsBlock
            values={formik.values}
            errors={formik.errors}
            redactionRegisterId={redactionRegisterId}
            isLoadingRedactedRegister={isLoadingRedactedRegister}
            onClose={onClose}
            open={open}
            isFileLoading={isFileLoading}
            onFileAccepted={onFileAccepted}
            onFileDelete={onFileDelete}
            handleChangeSelect={handleChangeSelect}
            iconUrl={currentIcon}
            role={role}
          />
        ),
        isError:
          !!formik.errors.banks ||
          (isArray(formik.errors.banks) &&
            formik.errors.banks.some(
              item =>
                !!item.bank_account_number ||
                !!item.bank_account_bsb ||
                !!item.bank_account_name ||
                !!item.bank_name,
            )),
      },
    ],
    [
      currentIcon,
      formik.errors,
      formik.values,
      handleChangeSelect,
      isFileLoading,
      isLoadingRedactedRegister,
      onClose,
      onFileAccepted,
      onFileDelete,
      open,
      redactionRegisterId,
      role,
    ],
  );

  const handleBlur = useCallback(() => {
    formik.setFieldValue('abn', formik.values.abn?.trim());
    formik.setFieldValue('ACN', formik.values.ACN?.trim());
    formik.setFieldValue(
      'banks',
      formik.values.banks.map(item => ({
        ...item,
        bank_account_bsb: item.bank_account_bsb?.trim(),
        bank_account_number: item.bank_account_number?.trim(),
        bank_account_name: item.bank_account_name?.trim(),
        bank_name: item.bank_name?.trim(),
      })),
    );
  }, [formik]);

  return (
    <ModalContentWrapper open={open} title={title} onClose={handleCloseModalWithResetForm}>
      <FormikProvider value={formik}>
        <form
          className="w-full flex flex-col space-y-10"
          onSubmit={preventFormDefault}
          onBlur={handleBlur}
        >
          <div className="w-full flex flex-col space-y-8">
            {role === 'issuer' || (role === 'admin' && !!redactionRegisterId) ? (
              <>
                <TabButtons
                  tabs={tabItems}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                  disabledTooltipId={disabledTooltipId}
                  className="w-full flex items-center justify-start space-x-6 relative border-b border-gray-200 pb-[12px]"
                />
                <Tabs activeTab={activeTab} items={tabItems} />
              </>
            ) : (
              <>
                {tabItems.map(({ container }, i) => (
                  <Fragment key={i}>{container}</Fragment>
                ))}
              </>
            )}
          </div>
          {activeTab === TabItem.BankAccounts && !formik.values.banks.length ? (
            <></>
          ) : (
            <Button
              disabled={isLoadingRedactedRegister || isRegisterUpdating || isRegisterCreation}
              onClick={() => {
                formik.setErrors({});
                setTimeout(() => formik.handleSubmit(), 1);
              }}
              type="submit"
              loading={formik.isSubmitting}
            >
              {isRedaction ? 'Save changes' : 'Confirm'}
            </Button>
          )}
        </form>
      </FormikProvider>
      <Tooltip id={disabledTooltipId} />
    </ModalContentWrapper>
  );
};

interface BlockProps extends AdminAddRegisterModalProps {
  isLoadingRedactedRegister: boolean;
  isFileLoading: boolean;
  onFileAccepted: (file: File) => Promise<void>;
  onFileDelete: () => void;
  handleChangeSelect: (field: string) => (option: string | boolean | null | any) => void;
  values: UpdateRegisterRequest & { currency?: CurrencyEnum };
  errors: FormikErrors<UpdateRegisterRequest & { currency?: CurrencyEnum }>;
  iconUrl?: string;
}

const GeneralBlock = ({
  role,
  redactionRegisterId,
  isLoadingRedactedRegister,
  isFileLoading,
  onFileAccepted,
  onFileDelete,
  handleChangeSelect,
  values,
  errors,
  iconUrl,
}: BlockProps) => {
  return (
    <>
      {role === 'admin' && (
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="name"
          placeholder="Defender Credit"
          label="name"
        />
      )}
      {!!redactionRegisterId && (
        <div className="w-full flex flex-col items-start justify-center gap-2">
          <div className="flex flex-col justify-center text-left gap-1">
            <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] font-medium uppercase">
              Image
            </span>
          </div>
          <div className="w-full">
            <Dropzone
              variant="icon"
              changeExpansionImage={true}
              acceptFormat={['jpeg', 'png']}
              maxSizeMb={1}
              onFileAccepted={onFileAccepted}
              loading={isFileLoading}
              value={values.icon_base64 || iconUrl}
              onFileClear={onFileDelete}
              error={errors.icon_base64 as string}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Select
          disabled={isLoadingRedactedRegister}
          value={values.type}
          label="register type"
          placeholder="Select register type"
          onChange={handleChangeSelect('type')}
          options={getSelectOptionByEnum<RegisterType>(RegisterType)}
          error={errors.type}
        />
        <span className="text-xs leading-5 text-gray-600">
          Determines naming of some transaction types. Buy back feature is only available to
          registers of Company type.
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="register_legal_name"
          label="Register Legal Entity Name"
          placeholder="ATF YOUR FUND"
        />
        <span className="text-xs leading-5 text-gray-600">
          A name of your fund legal entity shown on the header of the reports like Holding Statement
        </span>
      </div>
      <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="ACN"
          placeholder="123456789"
          label="ACN"
        />
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="abn"
          placeholder="01234567890"
          label="abn"
        />
      </div>
      {values.type === 'fund' && (
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="trustee"
          placeholder="TRUSTEE COMPANY NAME PTY LTD"
          label="trustee"
        />
      )}
      <TextInputField
        disabled={isLoadingRedactedRegister}
        name="registered_office"
        placeholder="100/10 Street Name, North Sydney NSW 1000"
        label="Registered Office Address"
      />
      <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
        <Select
          disabled={isLoadingRedactedRegister}
          value={values.country_code}
          placeholder="Select country code"
          label="Country code"
          onChange={handleChangeSelect('country_code')}
          options={Object.entries(CountryNameCodeEnum).map(item => ({
            label: (
              <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                <span className="text-gray-900">{item[1]}</span>
                <span className="text-gray-500">{fixNameEnum(item[0])}</span>
              </span>
            ),
            value: item[1],
            toString: () => fixNameEnum(item[0]),
          }))}
          error={errors.country_code}
        />
        <TooltipNew
          text="Сurrency can only be selected when creating a register"
          disabled={!redactionRegisterId}
          className="w-full"
        >
          <Select<CurrencyEnum>
            value={values?.currency}
            placeholder="Select currency"
            label="Currency"
            positionMenu="up"
            disabled={!!redactionRegisterId}
            onChange={handleChangeSelect('currency')}
            options={Object.entries(CurrencyEnum).map(item => ({
              label: (
                <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                  <span className="text-gray-900">{item[1].toUpperCase()}</span>
                </span>
              ),
              value: item[1],
              toString: () => fixNameEnum(item[0]),
            }))}
            error={String(errors?.currency || '') as string}
          />
        </TooltipNew>
      </div>
      <SelectButtons
        disabled={isLoadingRedactedRegister}
        label="allows fractional shares"
        options={sharesTypeOptions}
        selected={sharesTypeOptions.find(
          option => option.value === values.allows_fractional_shares,
        )}
        onChange={item => {
          if (item === true) {
            handleChangeSelect('rounding_type')(null);
          }
          handleChangeSelect('allows_fractional_shares')(item);
        }}
        error={errors?.allows_fractional_shares}
      />
      {!values.allows_fractional_shares && (
        <div className="flex flex-col gap-1">
          <Select
            disabled={isLoadingRedactedRegister}
            value={values.rounding_type}
            label="rounding type"
            placeholder="Select rounding type"
            onChange={handleChangeSelect('rounding_type')}
            options={getSelectOptionByEnum<RoundingType>(RoundingType)}
            error={errors.rounding_type}
          />
          <span className="text-xs leading-5 text-gray-600">
            Defines how dividend payments are converted into shares when fractional shares are not
            allowed. "Up" rounds up to the nearest whole share, while "Down" rounds down
          </span>
        </div>
      )}
      {!values.allows_fractional_shares && (
        <div className="flex flex-col gap-1">
          <SelectButtons
            disabled={isLoadingRedactedRegister}
            label="enable carried forward balance"
            options={sharesTypeOptions}
            selected={sharesTypeOptions.find(
              option => option.value === values.hold_carried_forward,
            )}
            onChange={handleChangeSelect('hold_carried_forward')}
          />
          <span className="text-xs leading-5 text-gray-600">
            If enabled, any carried-forward balance from previous Dividend Reinvestment Plans (DRPs)
            will be applied to future DRPs. If the dividend amount is insufficient to purchase a
            full share, the carried-forward balance will be added to the next dividend payout to
            facilitate share acquisition
          </span>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <TextInputField
          disabled={isLoadingRedactedRegister}
          name="user_identification_number"
          onChange={e => {
            const fieldName = e.target.name;
            const value = onlyNumbers(e.target.value, true);
            handleChangeSelect(fieldName)(value === '' ? null : value);
          }}
          label="User Identification Number (optional)"
          placeholder="123456"
          hintLabel="This number is used in generating the ABA file for dividend payments"
        />
        <span className="text-xs leading-5 text-gray-600">
          Must be User Identification Number which is allocated by APCA.
        </span>
      </div>
    </>
  );
};

const ContactBlock = ({ isLoadingRedactedRegister }: BlockProps) => {
  return (
    <>
      <TextInputField
        disabled={isLoadingRedactedRegister}
        name="email"
        placeholder="example@mail.com"
        label="email (optional)"
        hintLabel="This email will be displayed in reports in the contact information"
      />
      <TextInputField
        disabled={isLoadingRedactedRegister}
        name="phone"
        label="phone (optional)"
        placeholder="+61 0 8174 6817"
      />
    </>
  );
};

const BankDetailsBlock = ({
  redactionRegisterId,
  isLoadingRedactedRegister,
  handleChangeSelect,
  values,
  errors,
  role,
}: BlockProps) => {
  const isRedaction = !!redactionRegisterId;
  const handleChangeInputBank = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value;
      const newBanks = values.banks.map((bank, i) => {
        if (i === index) {
          return {
            ...bank,
            [name]: value,
          };
        }
        return bank;
      });
      handleChangeSelect('banks')(newBanks);
    },
    [handleChangeSelect, values.banks],
  );

  const handleChangeSelectBank = useCallback(
    (index: number, fieldName: string) => (value: any) => {
      const newBanks = values.banks.map((bank, i) => {
        if (i === index) {
          return {
            ...bank,
            [fieldName]: value,
          };
        }
        return bank;
      });
      handleChangeSelect('banks')(newBanks);
    },
    [handleChangeSelect, values.banks],
  );

  const handleChangeRegisterClass = useCallback(
    (index: number, fieldName: string) => (options: SelectPaginateOption<ClassItemOption>[]) => {
      const register_class_ids = options.map(v => v.value.id);
      handleChangeSelectBank(index, fieldName)(register_class_ids);
    },
    [handleChangeSelectBank],
  );

  const handleAddBank = useCallback(() => {
    const newBanks: BankModel[] = [
      ...values.banks,
      {
        bank_name: '',
        bank_account_number: '',
        bank_account_bsb: '',
        bank_account_name: '',
      } as BankModel,
    ];
    handleChangeSelect('banks')(newBanks);
  }, [handleChangeSelect, values.banks]);

  const handleDeleteBank = useCallback(
    (index: number) => () => {
      const newBanks = values.banks.filter((_, i) => i !== index);
      handleChangeSelect('banks')(newBanks);
    },
    [handleChangeSelect, values.banks],
  );

  return (
    <>
      <div className="overflow-y-auto w-full flex flex-col gap-8">
        {values.banks.length ? (
          <>
            {values.banks.map((bank, i) => {
              const error = errors.banks?.[i] as FormikErrors<BankModel>;
              return (
                <div key={i} className="w-full flex flex-col gap-8">
                  <div className="w-full flex gap-4 justify-between items-center">
                    <span>Bank #{i + 1}</span>
                    {values.banks.length - 1 > 0 && (
                      <span
                        onClick={handleDeleteBank(i)}
                        className="cursor-pointer hover:opacity-70"
                      >
                        <img src={TrashIcon} alt="delete" className="w-6 h-6 min-h-6 min-w-6" />
                      </span>
                    )}
                  </div>

                  <TextInput
                    name="bank_account_name"
                    placeholder="Your entity name"
                    label="bank account name"
                    onChange={handleChangeInputBank(i)}
                    value={bank?.bank_account_name}
                    error={error?.bank_account_name}
                  />

                  <Select
                    value={bank?.bank_types}
                    placeholder="Select bank types"
                    label="Bank types"
                    isMulti={true}
                    onChange={handleChangeSelectBank(i, 'bank_types')}
                    options={Object.entries(BankTypeEnum).map(item => ({
                      label: (
                        <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                          <span className="text-gray-900">{fixNameEnum(item[0])}</span>
                        </span>
                      ),
                      value: item[1],
                      toString: () => fixNameEnum(item[0]),
                    }))}
                    error={String(error?.bank_types || '') as string}
                  />

                  {isRedaction && role === 'issuer' && (
                    <RegisterClassesSelectPaginate
                      isMulti={true}
                      label="CLASSES"
                      onChange={handleChangeRegisterClass(i, 'bank_classes')}
                      value={bank?.bank_classes}
                      error={String(error?.bank_classes || '') as string}
                      registerId={redactionRegisterId}
                      showAddNewItem={true}
                      showPriceInLabel={false}
                    />
                  )}

                  <div className="flex flex-col gap-1">
                    <TextInput
                      name="bank_name"
                      label="Bank name"
                      placeholder="CBA"
                      onChange={handleChangeInputBank(i)}
                      value={bank?.bank_name}
                      error={error?.bank_name}
                    />
                    <span className="text-xs leading-5 text-gray-600">
                      Must be approved Financial Institution abbreviation. For example, Bank of
                      Queensland's abbreviation is BQL, Westpac's abbreviation is "WBC". Consult
                      your Bank for correct abbreviation.
                    </span>
                  </div>

                  <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
                    <TextInput
                      name="bank_account_number"
                      placeholder="1111 2222 4"
                      label="bank account number"
                      onChange={handleChangeInputBank(i)}
                      value={bank?.bank_account_number}
                      error={error?.bank_account_number}
                    />
                  </div>
                  <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
                    <TextInput
                      name="bank_account_bsb"
                      placeholder="123-123"
                      label="BSB"
                      onChange={handleChangeInputBank(i)}
                      value={bank?.bank_account_bsb}
                      error={error?.bank_account_bsb}
                    />
                    <TooltipNew
                      text="Сurrency can only be selected when creating a register"
                      disabled={!bank.id}
                      className="w-full"
                    >
                      <Select<CurrencyEnum>
                        value={bank?.bank_currency}
                        placeholder="Select currency"
                        label="Currency"
                        positionMenu="up"
                        onChange={handleChangeSelectBank(i, 'bank_currency')}
                        disabled={!!bank.id}
                        options={Object.entries(CurrencyEnum).map(item => ({
                          label: (
                            <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                              <span className="text-gray-900">{item[1].toUpperCase()}</span>
                              {/* <span className="text-gray-500">{item[0].toUpperCase()}</span> */}
                            </span>
                          ),
                          value: item[1],
                          toString: () => fixNameEnum(item[0]),
                        }))}
                        error={String(error?.bank_currency || '') as string}
                      />
                    </TooltipNew>
                  </div>
                </div>
              );
            })}
            <span className="w-[190px]">
              <Button variant="plain" className="px-0" onClick={handleAddBank}>
                <img src={PlusBlueIcon} alt="plus" className="pr-1" /> Add new bank account
              </Button>
            </span>
          </>
        ) : isRedaction ? (
          isLoadingRedactedRegister ? (
            <div className="flex flex-col w-full h-[200px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="flex flex-col w-full justify-center items-center gap-4 h-[300px]">
                <div className="py-3.5 px-3.5 rounded-full bg-gray-200">
                  <img src={BankBlackIcon} alt="Bank" className="w-9 h-9 min-w-9 min-h-9" />
                </div>
                <span className="font-[RecifeDisplay] text-2xl font-semibold text-gray-900">
                  No bank accounts yet
                </span>
                <span className="text-sm font-[Inter] leading-[22px] text-gray-400">
                  Create your first bank account
                </span>
                <Button
                  onClick={handleAddBank}
                  className="w-[154px]"
                  disabled={isLoadingRedactedRegister}
                >
                  Add bank account
                </Button>
              </div>
            </>
          )
        ) : (
          <span className="w-[190px]">
            <Button variant="plain" className="px-0" onClick={handleAddBank}>
              <img src={PlusBlueIcon} alt="plus" className="pr-1" /> Add new bank account
            </Button>
          </span>
        )}
      </div>
    </>
  );
};
